import { Controller } from "@hotwired/stimulus"

// Gives a form honest feedback while it works.
//
// Deliberately does NOT set `disabled` on the submit button: Turbo refuses to
// submit a form whose submit button is disabled, so doing that hides the label
// and then silently cancels the request. `aria-busy` plus pointer-events gives
// the same protection against double-clicks without breaking the submit.
export default class extends Controller {
  static targets = ["button", "progress", "step"]
  static values = {
    busyLabel: { type: String, default: "Working…" },
    steps: { type: Array, default: [] },
    stepInterval: { type: Number, default: 2200 }
  }

  connect() {
    this.timer = null
    this.submitted = false
  }

  disconnect() {
    this.stopStepping()
  }

  start(event) {
    // Guard against a second submit rather than disabling the control.
    if (this.submitted) {
      event.preventDefault()
      return
    }
    this.submitted = true

    if (this.hasButtonTarget) {
      const button = this.buttonTarget
      // A <button> CARRYING AN ICON needs its label swapped, not its
      // contents: textContent would take the SVG with it, and `value` on a
      // <button> is "" rather than undefined — so the old check took the
      // input branch and the busy label silently never appeared.
      //
      // An explicit label target settles which is which, and a button with
      // no icon needs no markup change.
      const label = button.querySelector("[data-submit-label]")

      if (label) {
        button.dataset.idleLabel = label.textContent
        label.textContent = this.busyLabelValue
      } else if (button.tagName === "INPUT") {
        button.dataset.idleLabel = button.value
        button.value = this.busyLabelValue
      } else {
        button.dataset.idleLabel = button.textContent
        button.textContent = this.busyLabelValue
      }
      button.setAttribute("aria-busy", "true")
      button.classList.add("is-busy")
      button.style.pointerEvents = "none"
    }

    if (this.hasProgressTarget) {
      this.progressTarget.hidden = false
      this.startStepping()
    }
  }

  startStepping() {
    if (!this.hasStepTarget || this.stepsValue.length === 0) return

    let index = 0
    this.stepTarget.textContent = this.stepsValue[index]

    this.timer = setInterval(() => {
      index += 1
      // Hold on the last message rather than looping — implying progress we
      // can't see would be worse than saying nothing.
      if (index >= this.stepsValue.length) {
        this.stopStepping()
        return
      }
      this.stepTarget.textContent = this.stepsValue[index]
    }, this.stepIntervalValue)
  }

  stopStepping() {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
  }
}
